import {Injectable} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {TranslocoService} from '@ngneat/transloco';
import {TimezoneService} from '../services/timezone.service';
import {PrimeNGConfig} from 'primeng/api';
import {Tenant} from '../model/tenant';
import {Organization} from '../model/organization';

@Injectable({
  providedIn: 'root'
})
export class LoggedUser {

  private readonly DEFAULT_LANGUAGE = 'es';
  private readonly DEFAULT_TIMEZONE = 'Europe/Madrid';

  user: User;
  userObservable: ReplaySubject<User>;
  userPreferences: ReplaySubject<any>;
  userTenants: Tenant[] = [];
  userOrganizations: Organization[] = [];

  constructor(private translocoService: TranslocoService,
              private timezoneService: TimezoneService,
              private config: PrimeNGConfig,
              private http: HttpClient) {
    this.userObservable = new ReplaySubject<User>();
    this.userPreferences = new ReplaySubject<any>();
    this.reloadUser();
  }

  public reloadUser(): void {
    this.http.get<any>('/catalog/me').toPromise().then(user => {
      this.user = user;
      this.userObservable.next(user);
      this.userTenants = user.tenants;
      this.userOrganizations = user.organizations;
      this.translocoService.setActiveLang(user.language ?? this.DEFAULT_LANGUAGE);
      this.timezoneService.setTimezone(user.timezone ?? this.DEFAULT_TIMEZONE);
      this.translocoService.selectTranslateObject('primeng').subscribe(primengTr => {
        this.config.setTranslation(primengTr);
      });
      this.reloadPreferences();
    })
      .catch((err) => this.userObservable.next(new User()));
  }

  public reloadPreferences(): void {
    this.http.get<any>('/catalog/me/preferences').toPromise().then(userData => {
      userData.preferences = this.loadDefaultPreferences(userData);
      this.userPreferences.next(userData);
    });
  }

  private loadDefaultPreferences(userData: any) {
    const defaultCustomStatusAssetRepresentation = {
      ok: '#23CD26',
      alarm: '#CD2323',
      warning: '#E1D019',
      inactivity: '#8F8F8F'
    };
    const defaultMapBoxConfig = { config: {cluster: true, clusterMaxZoom: 22, clusterRadius: 65 }};

    if (userData.preferences) {
      let preferencesObject = JSON.parse(userData.preferences);

      if (preferencesObject?.customStatusAssetRepresentation) {

        Object.keys(defaultCustomStatusAssetRepresentation).forEach((key) => {
          if (preferencesObject.customStatusAssetRepresentation[key] === undefined) {
            preferencesObject.customStatusAssetRepresentation[key] = defaultCustomStatusAssetRepresentation[key];
          }
        });

      } else {
        preferencesObject = {
          ...preferencesObject, customStatusAssetRepresentation: {
            ...defaultCustomStatusAssetRepresentation
          }
        };
      }

      if (preferencesObject?.mapbox?.config ) {
        Object.keys(defaultMapBoxConfig.config).forEach((key) => {
          if (preferencesObject.mapbox?.config[key] === undefined) {
            preferencesObject.mapbox.config[key] = defaultMapBoxConfig.config[key];
          }
        });
      } else {
        preferencesObject = {
          ...preferencesObject, mapbox: {
            ...defaultMapBoxConfig
          }
        };
      }

      return preferencesObject;
    } else {
      return {
        customStatusAssetRepresentation: { ...defaultCustomStatusAssetRepresentation },
        mapbox: { ...defaultMapBoxConfig }
      };
    }
  }
}


export class User {
  uuid: string;
  name: string;
  username: string;
  email: string;
  tenant: string;
  organization: string;
  role: string;
  language: string;
  timezone: string;
  portalUrl: string;
  tenants: Tenant[];
  organizations: Organization[];

  tenantUuid: string;
  organizationUuid: string;
}

export class Authority {
  authority: string;
}
